import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Receipt, Save, Plus, Filter, Eye, Trash2, Building2 } from 'lucide-react';
import { money, fmtDate } from '../lib/share';
import DocShare from './DocShare';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

// Facturación de licencias SalesPRO (solo Super Master / Right Hand)
export const VendorBilling = () => {
  const [s, setS] = useState(null);
  const [inv, setInv] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [period, setPeriod] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [lines, setLines] = useState([{ description: '', qty: 1, price: '' }]);
  // Filtros de lista
  const [fTenant, setFTenant] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fKind, setFKind] = useState('');
  // Modal detalle
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const params = {};
    if (fTenant) params.tenant_id = fTenant;
    if (fStatus) params.status = fStatus;
    if (fKind) params.kind = fKind;
    try {
      const [st, iv, tn] = await Promise.all([
        api.getVendorSettings(),
        api.getVendorInvoices(params),
        api.getTenants().catch(() => ({ data: [] })),
      ]);
      setS(st.data); setInv(iv.data || []); setTenants(tn.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fTenant, fStatus, fKind]);
  if (!s) return null;

  const setV = (grp, k) => (e) => setS({ ...s, [grp]: { ...s[grp], [k]: e.target.value } });
  const save = async () => {
    try { await api.saveVendorSettings({ master_fee: parseFloat(s.master_fee) || 0, branch_fee: parseFloat(s.branch_fee) || 0, vendor: s.vendor, client: s.client });
      toast.success('SalesPRO: Datos de facturación guardados'); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const create = async (kind, auto) => {
    try {
      await api.createVendorInvoice({
        kind, auto, period, tenant_id: tenantId || null,
        lines: lines.filter((l) => l.description).map((l) => ({ ...l, qty: parseFloat(l.qty) || 1, price: parseFloat(l.price) || 0 }))
      });
      toast.success('SalesPRO: Documento generado'); setLines([{ description: '', qty: 1, price: '' }]); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openDetail = async (iid) => {
    try { const r = await api.getVendorInvoice(iid); setDetail(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const F = ({ grp, k, l }) => <div><Label className="text-xs">{l}</Label><Input value={s[grp][k] || ''} onChange={setV(grp, k)} /></div>;

  return (
    <Card data-testid="vendor-billing">
      <CardContent className="p-5 space-y-4">
        <h3 className="font-heading font-bold text-[#022C22] flex items-center gap-2"><Receipt size={18} /> Facturación SalesPRO</h3>
        <p className="text-xs text-[#64748B]">Factura licencias a tus empresas afiliadas. Selecciona el tenant al que vas a facturar para pre-cargar sus datos.</p>

        {/* Datos del emisor (nuestra empresa SaaS) */}
        <details className="border rounded-lg p-3">
          <summary className="text-sm font-semibold text-[#022C22] cursor-pointer">Datos del emisor y tarifas base (expandir)</summary>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
            <div><Label className="text-xs">Licencia principal $/mes</Label><Input type="number" value={s.master_fee} onChange={(e) => setS({ ...s, master_fee: e.target.value })} data-testid="vendor-master-fee" /></div>
            <div><Label className="text-xs">Por sucursal extra $/mes</Label><Input type="number" value={s.branch_fee} onChange={(e) => setS({ ...s, branch_fee: e.target.value })} data-testid="vendor-branch-fee" /></div>
            <F grp="vendor" k="name" l="Nuestra empresa" /><F grp="vendor" k="rif" l="Nuestro RIF" />
            <F grp="vendor" k="phone" l="Teléfono" /><F grp="vendor" k="email" l="Correo" /><F grp="vendor" k="address" l="Dirección" /><F grp="vendor" k="bank_info" l="Datos bancarios" />
          </div>
          <Button variant="outline" onClick={save} className="mt-2" data-testid="vendor-save"><Save size={14} className="mr-1" /> Guardar datos</Button>
        </details>

        {/* Crear factura */}
        <div className="border-t pt-3 space-y-2">
          <p className="text-sm font-semibold text-[#022C22]">Generar documento</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs flex items-center gap-1"><Building2 size={12} /> Cliente (empresa licenciataria)</Label>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="w-full border rounded-md px-2 py-2 text-sm" data-testid="vendor-tenant-select">
                <option value="">— selecciona una empresa —</option>
                {tenants.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>{t.name} {t.vertical_label ? `· ${t.vertical_label}` : ''}</option>
                ))}
              </select>
            </div>
            <div><Label className="text-xs">Período</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Febrero 2026" data-testid="vendor-period" /></div>
            <div className="flex items-end"><Button onClick={() => create('factura', true)} data-testid="vendor-gen-monthly">Generar factura mensual automática</Button></div>
          </div>
          <p className="text-xs text-[#64748B]">Líneas adicionales (trabajos extra, upgrades, etc.):</p>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_100px] gap-2">
              <Input placeholder="Descripción" value={l.description} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} data-testid={`vendor-line-desc-${i}`} />
              <Input type="number" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
              <Input type="number" placeholder="$" value={l.price} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} data-testid={`vendor-line-price-${i}`} />
            </div>))}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setLines([...lines, { description: '', qty: 1, price: '' }])}><Plus size={14} /> Línea</Button>
            <Button variant="outline" size="sm" onClick={() => create('cotizacion', false)} data-testid="vendor-gen-quote">Generar cotización</Button>
            <Button variant="outline" size="sm" onClick={() => create('factura', false)}>Factura solo con estas líneas</Button>
          </div>
        </div>

        {/* Lista con filtros */}
        <div className="border-t pt-3 space-y-2">
          <p className="text-sm font-semibold text-[#022C22] flex items-center gap-1"><Filter size={12} /> Facturas emitidas ({inv.length})</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <select value={fTenant} onChange={(e) => setFTenant(e.target.value)} className="border rounded-md px-2 py-2 text-xs" data-testid="vendor-filter-tenant">
              <option value="">Todos los clientes</option>
              {tenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>)}
            </select>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="border rounded-md px-2 py-2 text-xs" data-testid="vendor-filter-status">
              <option value="">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="pagada">Pagada</option>
              <option value="anulada">Anulada</option>
            </select>
            <select value={fKind} onChange={(e) => setFKind(e.target.value)} className="border rounded-md px-2 py-2 text-xs">
              <option value="">Todos los tipos</option>
              <option value="factura">Factura</option>
              <option value="cotizacion">Cotización</option>
            </select>
          </div>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {inv.length === 0 && <p className="text-xs text-[#94A3B8]">Sin resultados.</p>}
            {inv.map((i) => (
              <div key={i.invoice_id} className="flex flex-wrap justify-between items-center gap-2 border rounded-lg p-2 text-sm hover:bg-[#F8FAFC]" data-testid={`vendor-invoice-${i.number}`}>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold text-[#022C22]">{i.number} <span className={`text-[10px] px-1.5 py-0.5 rounded ${i.status === 'pagada' ? 'bg-green-100 text-green-700' : i.status === 'anulada' ? 'bg-gray-200 text-gray-600' : 'bg-amber-100 text-amber-700'}`}>{i.status}</span></p>
                  <p className="text-[11px] text-[#64748B]">{i.kind} · {i.tenant_name || <span className="text-red-500">sin cliente</span>} · {i.period || '-'} · {fmtDate(i.created_at)} · <b>{money(i.total)}</b></p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openDetail(i.invoice_id)} data-testid={`vendor-detail-${i.number}`}><Eye size={12} /></Button>
                  <select value={i.status} onChange={(e) => api.setVendorInvoiceStatus(i.invoice_id, e.target.value).then(load).catch((er) => toast.error(er.response?.data?.detail || 'Error'))} className="text-xs border rounded px-1 py-1">
                    <option>pendiente</option><option>pagada</option><option>anulada</option>
                  </select>
                  <DocShare kind="vendor_invoice" refId={i.invoice_id} phone={s.client.phone} label={i.number} compact />
                </div>
              </div>))}
          </div>
        </div>

        {/* Modal detalle */}
        <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
          <DialogContent className="max-w-2xl" data-testid="vendor-detail-modal">
            <DialogHeader><DialogTitle>Detalle de {detail?.number}</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Cliente</Label><p className="font-semibold">{detail.tenant_name || '— sin cliente —'}</p></div>
                  <div><Label className="text-xs">Tipo</Label><p>{detail.kind}</p></div>
                  <div><Label className="text-xs">Período</Label><p>{detail.period || '-'}</p></div>
                  <div><Label className="text-xs">Estado</Label><p className="font-semibold">{detail.status}</p></div>
                  <div><Label className="text-xs">Emitida</Label><p>{fmtDate(detail.created_at)}</p></div>
                  <div><Label className="text-xs">Total</Label><p className="font-bold text-lg">{money(detail.total)}</p></div>
                </div>
                <div>
                  <Label className="text-xs">Líneas</Label>
                  <table className="w-full text-xs border rounded">
                    <thead className="bg-[#F8FAFC]"><tr><th className="text-left p-1">Descripción</th><th className="text-right p-1">Cant.</th><th className="text-right p-1">Precio</th><th className="text-right p-1">Subtotal</th></tr></thead>
                    <tbody>
                      {(detail.lines || []).map((ln, i) => (
                        <tr key={i} className="border-t"><td className="p-1">{ln.description}</td><td className="p-1 text-right">{ln.qty}</td><td className="p-1 text-right">{money(ln.price)}</td><td className="p-1 text-right">{money((ln.qty || 0) * (ln.price || 0))}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {detail.notes && <div><Label className="text-xs">Notas</Label><p className="text-xs text-[#64748B] whitespace-pre-wrap">{detail.notes}</p></div>}
                <div className="flex justify-end gap-2">
                  <DocShare kind="vendor_invoice" refId={detail.invoice_id} label={detail.number} />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export const ErrorLogs = () => {
  const [l, setL] = useState([]);
  const load = () => api.getErrorLogs().then((r) => setL(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const clearAll = async () => {
    if (!window.confirm('¿Borrar TODOS los errores? Esta acción no se puede deshacer.')) return;
    try { const r = await api.clearErrorLogs(); toast.success(`SalesPRO: ${r.data.deleted} errores eliminados`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const clearTest = async () => {
    try { const r = await api.clearTestErrorLogs(); toast.success(`SalesPRO: ${r.data.deleted} entradas de prueba eliminadas`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  return (
    <Card data-testid="error-logs"><CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-[#022C22]">Registro de errores del sistema ({l.length})</h3>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={clearTest} data-testid="clear-test-errors" title="Elimina entradas TEST_*">Limpiar pruebas</Button>
          <Button size="sm" variant="outline" onClick={clearAll} className="text-red-600 border-red-200" data-testid="clear-all-errors"><Trash2 size={12} className="mr-1" /> Limpiar todo</Button>
        </div>
      </div>
      <p className="text-[11px] text-[#64748B] mb-2">Aquí ves errores reales de la aplicación (backend o frontend). Las entradas que comienzan con "TEST_" son del equipo QA interno y pueden ignorarse / limpiarse.</p>
      <div className="max-h-64 overflow-y-auto text-xs space-y-1">
        {l.length === 0 ? <p className="text-[#94A3B8]">Sin errores registrados.</p> : l.map((e) => (
          <div key={e.error_id} className="border-b py-1"><b className={e.source === 'backend' ? 'text-red-600' : 'text-amber-700'}>{e.source}</b> · {new Date(e.created_at).toLocaleString()} · {e.path}<br /><span className="text-[#64748B]">{e.message}</span></div>))}
      </div>
    </CardContent></Card>
  );
};

export default VendorBilling;
